import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import { StatisticsCards } from '@app/components/medical-dashboard/statisticsCards/StatisticsCards';
import { useResponsive } from '@app/hooks/useResponsive';
import * as S from './DashboardPage.styles';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { DashBoardClock } from '@app/components/Dashboard/Clock/DashBoardClock';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { DashBoardClockDigital } from '@app/components/Dashboard/Clock/DashBoardClockDigital';
import { BloodScreeningCard } from '@app/components/medical-dashboard/bloodScreeningCard/BloodScreeningCard/BloodScreeningCard';
import { DocumentExpiryCard } from '@app/components/medical-dashboard/DocumentExpiryCard/DocumentExpiryCard';
import { getDashboardMetaData } from '@app/api/dashbboard.api';
import { FirmCards } from '@app/components/medical-dashboard/statisticsCards/FirmCards';
import { EmployeeTradeCard } from '@app/components/medical-dashboard/EmployeeTradeCard/EmployeeTradeCard';
import { FirmDocumentExpiryCard } from '@app/components/medical-dashboard/FirmExpiryCard/FirmDocumentExpiryCard';
import { getFirmById } from '@app/api/firm.api';
import { daysRemainingFromToday } from '@app/utils/utils';

interface IDocumentMeta {
  value: number;
  name: string;
  description: string;
}

const DashboardPage: React.FC = () => {
  const { isDesktop } = useResponsive();
  const clock = useAppSelector((state) => state.clock.clock);
  const user = useAppSelector((state) => state.user.user);
  const FirmId = user?.AllowedFirms.find((obj) => obj.value === user?.DefualtFirmId)?.value || null;
  const [EdeData, setEdeData] = useState<IDocumentMeta[]>([]);
  const [rawData, setrawData] = useState([]);

  const [activeItem, setActiveItem] = useState<string>('');
  // eslint-disable-next-line
  const [employeeData, setEmployeeData] = useState<any>(null);
  // eslint-disable-next-line
  const [FirmData, setFirmData] = useState<any>(null);

  const fetch = useCallback(async (FirmId: number) => {
    await getDashboardMetaData(FirmId)
      .then((res) => {
        if (res.DocumentExpiryMeta.length > 0) {
          setrawData(res.DocumentExpiryMeta);
          const transformData: IDocumentMeta[] = Object.entries(
            // eslint-disable-next-line
            res.DocumentExpiryMeta.reduce((acc: any, curr: any) => {
              acc[curr.Type] = (acc[curr.Type] || 0) + 1;
              return acc;
            }, {}),
          ).map(([name, value]) => ({ name, value: Number(value), description: '' })); // Add description here if needed

          setEdeData(transformData);
        } else {
          setEdeData([
            { name: 'Passport', value: 0, description: '' },
            { name: 'Visa', value: 0, description: '' },
            { name: 'Emirate Id', value: 0, description: '' },
            { name: 'Labour Card', value: 0, description: '' },
            { name: 'Driving Licence', value: 0, description: '' },
          ]);
        }
      })
      .catch((e) => {
        console.log(e);
      });

    const FIrmData = await await getFirmById(FirmId);
    const FirmDocumentData = {
      'WC Insurance': FIrmData.WcExpiryDate ? daysRemainingFromToday(new Date(FIrmData.WcExpiryDate)) : 0,
      'Tenancy Contract': FIrmData.TenancyExpiryDate ? daysRemainingFromToday(new Date(FIrmData.TenancyExpiryDate)) : 0,
      'Trade Licence': FIrmData.TradeExpiryDate ? daysRemainingFromToday(new Date(FIrmData.TradeExpiryDate)) : 0,
      'Owner Passport': FIrmData.OwnerPassportExpiryDate
        ? daysRemainingFromToday(new Date(FIrmData.OwnerPassportExpiryDate))
        : 0,
      'Owner Visa': FIrmData.OwnerVisaExpiryDate ? daysRemainingFromToday(new Date(FIrmData.OwnerVisaExpiryDate)) : 0,
      'Sponsor Passport': FIrmData.SponsorPassportExpiryDate
        ? daysRemainingFromToday(new Date(FIrmData.SponsorPassportExpiryDate))
        : 0,
    };

    setFirmData(FirmDocumentData);
  }, []);

  useEffect(() => {
    fetch(FirmId || 0);
  }, [fetch, FirmId]);
  const { t } = useTranslation();

  const fnactiveItem = useCallback(
    (data: string) => {
      setActiveItem(data);
    },
    [setActiveItem],
  );

  const desktopLayout = (
    <BaseRow>
      <S.LeftSideCol xl={16} xxl={17}>
        <BaseRow gutter={[30, 30]}>
          <BaseCol span={24}>
            <BaseRow gutter={[30, 30]}>
              <FirmCards />
            </BaseRow>
          </BaseCol>
          <BaseCol span={24}>
            <BaseRow gutter={[30, 30]}>
              <StatisticsCards fnsetempdata={setEmployeeData} />
            </BaseRow>
          </BaseCol>
          <BaseCol xl={12}>
            <DocumentExpiryCard EdeData={EdeData} onDocClick={fnactiveItem} />
          </BaseCol>
          <BaseCol xl={12}>
            <FirmDocumentExpiryCard EdeData={FirmData} />
          </BaseCol>
          <BaseCol xl={24}>
            <EmployeeTradeCard Empdata={employeeData} />
          </BaseCol>
        </BaseRow>
      </S.LeftSideCol>

      <S.RightSideCol xl={8} xxl={7}>
        <div id="clock">{clock === 'analog' ? <DashBoardClock /> : <DashBoardClockDigital />}</div>
        <br />
        <div id="blood-screening">
          <BloodScreeningCard ActiveItem={activeItem} rawData={rawData} />
        </div>
        <S.Space />
        {/* <S.ScrollWrapper id="activity-story">
          <ActivityStory />
        </S.ScrollWrapper> */}
      </S.RightSideCol>
    </BaseRow>
  );

  const mobileAndTabletLayout = (
    <>
      <BaseRow gutter={[20, 20]}>
        <BaseCol span={24}>
          <BaseRow gutter={[30, 30]}>
            <FirmCards />
          </BaseRow>
        </BaseCol>
        <StatisticsCards fnsetempdata={setEmployeeData} />
      </BaseRow>
      <BaseRow>
        <BaseCol id="docactivity" span={24}>
          <DocumentExpiryCard EdeData={EdeData} onDocClick={fnactiveItem} />
        </BaseCol>
        <BaseCol id="activity" span={24}>
          <BloodScreeningCard ActiveItem={activeItem} rawData={rawData} />
        </BaseCol>
        <BaseCol id="activity" span={24}>
          <EmployeeTradeCard Empdata={employeeData} />
        </BaseCol>
      </BaseRow>
    </>
  );

  return (
    <>
      <PageTitle>{t('common.dashboard')}</PageTitle>
      {isDesktop ? desktopLayout : mobileAndTabletLayout}
    </>
  );
};

export default DashboardPage;
